import { useEffect, useMemo, useState } from 'react';
import './AddBookingToHomeScreenHint.css';

const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

const getPlatform = () => {
    const ua = window.navigator.userAgent || '';
    if (/iphone|ipad|ipod/i.test(ua) && !window.MSStream) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'other';
};

const AddBookingToHomeScreenHint = ({ companyUuid }) => {
    const [installEvent, setInstallEvent] = useState(null);
    const [visible, setVisible] = useState(false);
    const platform = useMemo(getPlatform, []);
    const dismissKey = `colmeia_booking_a2hs_dismissed_${companyUuid}`;

    useEffect(() => {
        let dismissed = false;
        try {
            dismissed = localStorage.getItem(dismissKey) === 'true';
        } catch {
            // Sem armazenamento disponível, a instrução continua utilizável nesta visita.
        }
        if (isStandalone() || dismissed) return undefined;

        if (platform === 'ios' || platform === 'android') setVisible(true);

        const onBeforeInstall = (event) => {
            event.preventDefault();
            setInstallEvent(event);
            setVisible(true);
        };
        const onInstalled = () => setVisible(false);
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, [dismissKey, platform]);

    const dismiss = () => {
        try {
            localStorage.setItem(dismissKey, 'true');
        } catch {
            // O fechamento ainda funciona quando o navegador bloqueia o armazenamento.
        }
        setVisible(false);
    };

    const install = async () => {
        if (!installEvent) return;
        installEvent.prompt();
        try {
            const choice = await installEvent.userChoice;
            if (choice?.outcome === 'accepted') setVisible(false);
        } catch {
            // O navegador pode fechar o prompt sem retornar uma escolha.
        }
        setInstallEvent(null);
    };

    if (!visible) return null;

    const instruction = !installEvent && platform === 'ios'
        ? <>Toque em <i className="fa-solid fa-arrow-up-from-bracket" /> Compartilhar e depois em “Adicionar à Tela de Início”.</>
        : !installEvent && platform === 'android'
            ? <>Abra o menu <i className="fa-solid fa-ellipsis-vertical" /> do navegador e escolha “Instalar app” ou “Adicionar à tela inicial”.</>
            : <>Tenha o agendamento sempre à mão na tela inicial do seu celular.</>;

    return (
        <aside className="booking-install-hint" aria-label="Instalar agendamento na tela inicial">
            <i className="fa-solid fa-mobile-screen-button booking-install-hint__icon" aria-hidden="true" />
            <div className="booking-install-hint__text">
                <strong>Instale o agendamento no celular</strong>
                <span>{instruction}</span>
            </div>
            {installEvent && (
                <button type="button" className="booking-install-hint__button" onClick={install}>
                    Instalar
                </button>
            )}
            <button
                type="button"
                className="booking-install-hint__close"
                onClick={dismiss}
                aria-label="Fechar instrução de instalação"
            >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
        </aside>
    );
};

export default AddBookingToHomeScreenHint;
